import { defineAsyncComponent } from "vue";
import { createRouter, createWebHashHistory, RouteRecordRaw } from "vue-router";
import AuthenticationHelper from "../helper/OIDCManager";
import { appAbility } from "../services/Ability";
import { useFeatureLot } from "../composables/FeatureLotComposable";
import { useUsersStore } from "../store/users.store";
import { useSanitaryContext } from "../store/sanitaire/sanitaryContext.store";
import { reloadOnceAfterDeploy, isChunkLoadError } from "../helper/deployReload";
import { ENV_NAME } from "../services/config";

const routes = [
  {
    name: "Home",
    path: "/",
    component: () => import("../components/Home/Home.vue"),
    meta: {
      requiresAuth: true, //A rajouter pour toutes les routes qui nécessitent l'authentification
    },
  },
  {
    name: "AdminResync",
    path: "/admin/resync",
    component: () => import("../views/Admin/AdminResync.vue"),
    meta: {
      requiresAuth: true, // console d'administration : resynchronisation des données
      requiresRole: "GDPORC_ResponsableApplicatif", // réservé au responsable applicatif (gardé aussi côté API)
    },
  },
  {
    name: "AdminTestOrder",
    path: "/admin/commandes-test",
    component: () => import("../views/Admin/AdminTestOrder.vue"),
    meta: {
      requiresAuth: true, // fabrique de commandes client de test (develop / recette)
      requiresRole: "GDPORC_ResponsableApplicatif", // gardé aussi côté API, qui renvoie 404 en production
      notInProduction: true,
    },
  },
  {
    name: "AdminCatalogueSanitaire",
    path: "/GestionCatalogueSanitaire",
    component: () => import("../views/Admin/AdminCatalogueSanitaire.vue"),
    meta: {
      requiresAuth: true, // console d'administration : catalogue sanitaire (médicaments, pathologies)
      requiresRole: "GDPORC_ResponsableApplicatif", // pas encore de droit CASL seedé côté API pour cette page
      lot: "LOT11",
    },
  },
  {
    name: "Sanitaire",
    path: "/Sanitaire",
    component: () => import("../views/Pointage/PointageSanitaire.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    // Mes animaux, vue ATE (SFD-11 Q20, N05) : même composant que /ListeAnimaux, ouvert avec la prop
    // vue = "ATE" (colonnes SANITAIRE figées + actions sanitaires). Le chemin fait le droit
    // global#page#ListeAnimauxSanitaire, amorcé par la migration AmorceHabilitationsMesAnimauxSanitaire.
    name: "ListeAnimauxSanitaire",
    path: "/ListeAnimauxSanitaire",
    component: () => import("../views/Animal/Animal.vue"),
    props: { vue: "ATE" },
    meta: {
      requiresAuth: true,
      // Pas encore ouverte en production : la mutation interne ne porte ni date d'effet ni agent (R08).
      notInProduction: true,
      lot: "LOT11",
      // Instance distincte de /ListeAnimaux : sinon les filtres passent d'une liste à l'autre.
      isolateInstance: true,
    },
  },
  {
    // Ancienne adresse de la vue ATE : les favoris continuent de fonctionner. Le garde lit la meta de
    // la route CIBLE (ListeAnimauxSanitaire), pas celle de /ListeAnimaux.
    path: "/GestionATE",
    redirect: { name: "ListeAnimauxSanitaire" },
  },
  {
    name: "Animal",
    path: "/ListeAnimaux",
    component: () => import("../views/Animal/Animal.vue"),
    meta: {
      requiresAuth: true, //A rajouter pour toutes les routes qui nécessitent l'authentification
      isolateInstance: true,
    },
  },
  {
    name: "Production",
    path: "/Production",
    component: () => import("../views/Production/SearchModal/SearchAnimalModal.vue"),
    meta: {
      requiresAuth: false, //A rajouter pour toutes les routes qui nécessitent l'authentification
      lot : "LOT2"
    },
  },
  {
    name:"Collaborateur",
    path : "/ListeCollaborateurs",
    component : () => import("../views/Agent/AgentListView.vue"),
    meta : {
      requiresAuth: false,
      lot : "LOT2"
    }
  },
  {
    name:"TypePoudres",
    path: "/ListeTypePoudres",
    component : () => import("../views/PowderType/PowderTypeListView.vue"),
    meta : {
      requiresAuth: false, // TODO
      lot : "LOT2"
    }
  },
  {
    name:"Dilueur",
    path: "/ListeDilueurs",
    component : () => import("../views/DilutorBath/DilutorBathListView.vue"),
    meta : {
      requiresAuth: false, // TODO
      lot : "LOT2"
    }
  },
  {
    name: "ListeEvenementsCollecte",
    path: "/ListeEvenementsCollecte",
    component: () => import("../views/SamplingEvent/SamplingEventListView.vue"),
    meta: {
      requiresAuth: true, // console d'administration
      requiresRole: "GDPORC_Administrateur", // réservé au responsable applicatif
    },
  },
  {
    name: "ListeTypesMouvement",
    path: "/ListeTypesMouvement",
    component: () => import("../views/MovementType/MovementTypeListView.vue"),
    meta: {
      requiresAuth: true, // console d'administration
      requiresRole: "GDPORC_Administrateur", // réservé au responsable applicatif
    },
  },
  {
    name:"EtalonnagePhotocolorimetre",
    path: "/EtalonnagePhotocolorimetre",
    component : () => import("../views/Photocolorimeter/PhotocolorimeterCalibration.vue"),
    meta : {
      requiresAuth: true, // TODO
      lot : "LOT2"
    }
  },
  {
    name:"ListeEvenementsPrelevement",
    path: "/ListeEvenementsPrelevement",
    component : () => import("../views/EventsProd/EventsProdListView.vue"),
    meta : {
      requiresAuth: true, // TODO
      lot : "LOT2"
    }
  },
  {
    name:"ControlDilueur",
    path: "/ControlDilueurs",
    component : () => import("../views/DilutorControl/DilutorControlListView.vue"),
    meta : {
      requiresAuth: true, // TODO
      lot : "LOT2"
    }
  },
  {
    name: "TiersProvider",
    path: "/ListeProvider",
    component: defineAsyncComponent(
      () => import("../views/ThirdParty/ThirdParties.vue")
    ),
    props: { TypeTiers: "PROVIDER" },
    meta: {
      requiresAuth: true, //A rajouter pour toutes les routes qui nécessitent l'authentification
    },
  },
  {
    name: "ListeReserve",
    path: "/ListeReserve",
    component: defineAsyncComponent(
      () => import("../views/Reservation/Reservation.vue")
    ),
    meta: {
      requiresAuth: true, //A rajouter pour toutes les routes qui nécessitent l'authentification
    },
  },
  {
    name:'ListeCaryotype',
    path: '/ListeCaryotype',
    component: defineAsyncComponent(() => import('../views/Karyotype/Karyotype.vue')),
    meta: {
      requiresAuth: false, //A rajouter pour toutes les routes qui nécessitent l'authentification
    }
  },
  {
    name:'ListeLames',
    path: '/ListeLames',
    component: defineAsyncComponent(() => import('../views/Blade/BladeListView.vue')),
    meta: {
      requiresAuth: false, //A rajouter pour toutes les routes qui nécessitent l'authentification
      lot : "LOT2"
    }
  },
  {
    name: "TiersOwner",
    path: "/ListeOwner",
    component: defineAsyncComponent(
      () => import("../views/ThirdParty/ThirdParties.vue")
    ),
    props: { TypeTiers: "OWNER" },
    meta: {
      requiresAuth: true, //A rajouter pour toutes les routes qui nécessitent l'authentification
    },
  },
  {
    name: "Centre",
    path: "/ListeCentres",
    component: () => import("../views/Center/Center.vue"),
    meta: {
      requiresAuth: true, //A rajouter pour toutes les routes qui nécessitent l'authentification
    },
  },
  {
    name: "ListeParametres",
    path: "/ListeParametres",
    component: () => import("../views/Parameter/ParameterListView.vue"),
  },
  {
    name: "InfoUser",
    path: "/InfoUser",
    component: () => import("../views/User/User.vue"),
  },
  {
    name: "OptionUser",
    path: "/OptionUser",
    component: () => import("../views/Options/OptionsListView.vue"),
    meta: {
      requiresAuth: true, //A rajouter pour toutes les routes qui nécessitent l'authentification
    },
  },
  {
    name: "ListeCTG",
    path: "/ListeCTG",
    component: () => import("../views/CTG/CTG.vue"),
    meta: {
      requiresAuth: true, //A rajouter pour toutes les routes qui nécessitent l'authentification
    },
  },
  {
    name: "ListeInterventions",
    path: "/ListeInterventions",
    component: () => import("../views/Intervention/Intervention.vue"),
    meta: {
      requiresAuth: true, //A rajouter pour toutes les routes qui nécessitent l'authentification
    },
  },
  {
    name: "ListeElevage",
    path: "/ListeElevage",
    component: () => import("../views/BreedingStud/Elevage.vue"),
    meta: {
      requiresAuth: true, //A rajouter pour toutes les routes qui nécessitent l'authentification
    },
  },
  {
    name: "ListeMutation",
    path: "/ListeMutation",
    component: () => import("../views/MutationAnimal/MutationAnimal.vue"),
    meta: {
      requiresAuth: true, //A rajouter pour toutes les routes qui nécessitent l'authentification
    },
  },
  {
    name: "ListeSyneliaConnect",
    path: "/ListeSyneliaConnect",
    component: () => import("../components/ListeSyneliaConnect.vue"),
    meta: {
      requiresAuth: true, //A rajouter pour toutes les routes qui nécessitent l'authentification
    },
  },
  {
    name: "Components",
    path: "/Components",
    component: defineAsyncComponent(
      () => import("../views/Utilities/Components.vue")
    ),
    meta: {
      requiresAuth: true, //A rajouter pour toutes les routes qui nécessitent l'authentification
    },
  },
  {
    name: "TestMesure",
    path: "/TestMesure",
    component: defineAsyncComponent(
      () => import("../components/TestMesure.vue")
    ),
    meta: {
      requiresAuth: false, //A rajouter pour toutes les routes qui nécessitent l'authentification
    },
  },
  {
    name: "Reforms",
    path: "/ListeReformes",
    component: () => import("../views/Reform/ReformListView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    name: "ListeCommande",
    path: "/ListeCommande",
    component: defineAsyncComponent(() => import("../views/Order/Order.vue")),
    meta: {
      requiresAuth: true, //A rajouter pour toutes les routes qui nécessitent l'authentification
    },
  },
  {
    name: "ListeHistoriqueStatutCommande",
    path: "/ListeHistoriqueStatutCommande",
    component: defineAsyncComponent(
      () => import("../views/OrderStatusHistory/OrderStatusHistory.vue")
    ),
    meta: {
      requiresAuth: true, //A rajouter pour toutes les routes qui nécessitent l'authentification
    },
  },
  {
    name: "PreparationReforms",
    path: "/PreparationReformes",
    component:
      () => import("../views/Reform/PreparationListView.vue"),
    meta: {
      requiresAuth: false,
    },
  },
  {
    name: "ListePrelevements",
    path: "/ListePrelevements",
    component: () => import("../views/Ejaculate/EjaculateListView.vue"),
    meta : {
      requiresAuth: true,
      lot:"LOT2"
    }
  },
  {
    name: "ListeHeterospermie",
    path: "/ListeHeterospermie",
    component: () => import("../views/Fabrication/HeterospermieListView.vue"),
    meta : {
      requiresAuth: true,
      lot : "LOT3"
    }
  },
  {
    name: "ListeConsultationStock",
    path: "/ListeConsultationStock",
    component: () => import("../views/Stock/StockView.vue"),
    meta : {
      requiresAuth: true,
      lot: "LOT3",
    }
  },
  {
    name: "HistoriqueMouvementsStock",
    path: "/HistoriqueMouvementsStock",
    component: () => import("../views/Stock/StockView.vue"),
    props: { initialMode: "historique" },
    meta: {
      requiresAuth: true,
      lot: "LOT5",
    }
  },
  {
    name: "GestionCodesStock",
    path: "/GestionCodesStock",
    component: () => import("../views/Stock/CodeStockListView.vue"),
    meta: {
      requiresAuth: true,
    }
  },
  {
    name: "SuiviFabrication",
    path: "/SuiviFabrication",
    component: () => import("../views/Fabrication/suiviFabrication/SuiviFabricationListView.vue"),
    meta : {
      requiresAuth: true,
      lot:"LOT3"
    }
  },
  {
    name: "SuiviQualite",
    path: "/SuiviQualite",
    component: () => import("../views/Quality/QualityView.vue"),
    meta : {
      requiresAuth: true,
      lot : "LOT2"
    }
  },
  {
    name: "PlanMonte",
    path: "/PlanMonte",
    component: () => import("../views/MountingPlan/MountingPlanView.vue"),
    meta : {
      requiresAuth: true,
      lot: "LOT4"
    },
  },
  {
    name: "ListeBatiments",
    path: "/ListeBatiments",
    component: () => import("../views/Building/BuildingListView.vue"),
    meta : {
      requiresAuth: true,
      lot:"LOT4"
    }
  },
  {
    name: "ListeDevices",
    path: "/ListeDevices",
    component: () => import("../views/Device/DeviceListView.vue"),
    meta : {
      requiresAuth: true,
      lot : "LOT2"
    }
  },
  {
    name: "GestionRessources",
    path: "/GestionRessources",
    component: () => import("../views/Resources/ResourceManagement.vue"),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: "Compteurs",
    path: "/Compteurs",
    component: () => import("../views/Compteurs/CompteursView.vue"),
    meta: {
      requiresAuth: true,
      lot: "LOT9",
    }
  },
  {
    name: "ListePharmacie",
    path: "/ListePharmacie",
    component: () => import("../views/Pharmacy/PharmacyListView.vue"),
    meta: {
      requiresAuth: true
    }
  },
  {
    // Évènements station (E31-F02-U04, SFD-09) : le chemin fait le droit global#page#ListeEvenementsStation,
    // amorcé par la migration AmorceHabilitationsEvenementsStation.
    name: "ListeEvenementsStation",
    path: "/ListeEvenementsStation",
    component: () => import("../views/Sanitaire/StationEventListView.vue"),
    meta: {
      requiresAuth: true,
      lot: "LOT11",
    }
  },
  {
    name: "TestPerformance",
    path: "/TestPerfs",
    component: () => import("../views/TestPerfs/PerfStore.vue"),
    meta : {
      requiresAuth: false,
    }
  },
  {
    name: "TestClaude",
    path: "/TestClaude",
    component: () => import("../views/TestPerfs/ClaudeStore.vue"),
    meta : {
      requiresAuth: false,
    }
  },
  {
    name: "signin",
    path: "/signin-oidc",
    component: () => import("../views/User/SigninOidc.vue"),
  },
  {
    name: "signout",
    path: "/signout-oidc",
    component: () => import("../views/User/SignoutOidc.vue"),
  },
  {
    name: "silent-callback",
    path: "/silent-callback",
    component: () => import("../views/User/SilentCallback.vue"),
  },
  {
    name: "404NotFound",
    path: "/:pathMatch(.*)*",
    component: () => import("../views/StatusCodePage/404NotFound.vue"),
  },
  {
    name: "403Forbidden",
    path: "/403Forbidden",
    component: () => import("../views/StatusCodePage/403Forbidden.vue"),
  },
  {
    name: "403Admin",
    path: "/403Admin",
    component: () => import("../views/StatusCodePage/403Admin.vue"),
  },
  {
    name: "StartupError",
    path: "/StartupError",
    component: () => import("../views/StatusCodePage/StartupError.vue"),
  },
];

const router = createRouter({ history: createWebHashHistory(), routes });
router.beforeEach(async (to, from) => {

  // Authentification : uniquement sur les routes protégées
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    let user = await AuthenticationHelper._userManager.getUser();

    // Si l'utilisateur n'est pas connecté, on le redirige vers l'écran de connexion
    if (!user) {
      await AuthenticationHelper._userManager.signinRedirect();
      return false;
    }

    // Si le token est expiré, on tente un silent refresh avant de continuer
    if (user.expired) {
      try {
        await AuthenticationHelper._userManager.signinSilent();
      } catch (error) {
        console.error("Silent refresh échoué", error);
        await AuthenticationHelper._userManager.signinRedirect();
        return false;
      }
    }

    // Pas de vérification de droit pour la page d'accueil
    if (to.path === "/") {
      return true;
    }
  }

  // Droits CASL : sur toutes les routes sauf les pages d'auth, d'erreur et silent-callback
  const name = to.name?.toString();
  const isAuthRoute = name === "signin" || name === "signout" || name === "silent-callback";
  const isErrorRoute = name?.includes("403") || name?.includes("404") || name === "StartupError";

  if (!isAuthRoute && !isErrorRoute) {
    if (appAbility.rules.length === 0) {
      return { name: "403Admin" };
    }
    if (name !== "Home") {
      // Outils de test (ex fabrique de commandes client) : inaccessibles en production, y compris
      // par saisie directe de l'URL. L'API renvoie de toute façon 404 hors develop/recette.
      if (to.meta.notInProduction && ENV_NAME?.toUpperCase() === "PRODUCTION") {
        return { name: "403Forbidden" };
      }
      // Routes réservées à un rôle (ex console admin) : gardées par rôle OIDC, pas par
      // resource CASL (qui n'est pas seedée pour ces pages). Vérifié aussi côté API.
      const requiredRole = to.meta.requiresRole as string | undefined;
      if (requiredRole) {
        const roles = useUsersStore().currentUser?.roles ?? [];
        if (!roles.includes(requiredRole)) {
          return { name: "403Forbidden" };
        }
      } else if (to.meta.requiresAuth === true && !appAbility.can("access", "global#page#" + to.path.substring(1))) {
        return { name: "403Forbidden" };
      }
    }

    // Feature-gating par lot : vérifie que le centre actif a accès au lot de la route
    if (to.meta.lot) {
      const { canAccessRoute } = useFeatureLot();
      if (!canAccessRoute(to.meta)) {
        return { name: "403Forbidden" };
      }
    }
  }
});

// SFD-07 R15 — l'agent de saisie sanitaire est perdu dès qu'on quitte le contexte sanitaire.
// Dans afterEach et non dans le garde : le garde sort plus tôt sur certaines routes (`return true`
// de l'accueil), et une navigation annulée ou interrompue (`failure`) laisse l'opérateur sur son
// écran. Une redirection (403...) aboutit, elle, sur l'écran cible : c'est lui qui est jugé.
// Contexte reconnu sur le NOM de la route, insensible aux paramètres et à la casse de l'URL.
router.afterEach((to, _from, failure) => {
  if (!failure) {
    useSanitaryContext().exitIfOutsideContext(to.name);
  }
});

// Récupération auto après déploiement (Mantis 0028073) : si le chargement d'une vue lazy
// échoue parce que son chunk hashé n'existe plus (nouvelle version déployée), on force un
// rechargement unique pour récupérer la version courante au lieu d'une navigation cassée.
router.onError((error) => {
  if (isChunkLoadError(error)) {
    reloadOnceAfterDeploy("router.onError:chunk");
  }
});

export default router;
